import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable } from "@/components/common/DataTable";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useUrlState } from "@/lib/urlState";
import { queryKeys } from "@/lib/queryKeys";
import { exportRowsAsCsv } from "@/lib/csv";

interface EmailCampaign {
  id: string;
  name: string;
  status: string;
  frequency: string;
  scheduled_at: string | null;
  template_id: string;
  target_users: string[] | null;
  created_at: string;
  email_templates?: { name: string } | null;
}

// Status values match the `email_campaign_status` enum in Supabase.
const STATUS_OPTIONS = [
  "all",
  "draft",
  "scheduled",
  "active",
  "sending",
  "sent",
  "paused",
  "completed",
  "failed",
  "cancelled",
] as const;
type CampaignStatus = Exclude<(typeof STATUS_OPTIONS)[number], "all">;

// Color-coded badge per spec (green=success, yellow=in-flight, red=failure-ish).
const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    sent: "bg-green-50 text-green-700 border-green-200",
    sending: "bg-blue-50 text-blue-700 border-blue-200",
    scheduled: "bg-yellow-50 text-yellow-700 border-yellow-200",
    draft: "bg-gray-50 text-gray-700 border-gray-200",
    paused: "bg-orange-50 text-orange-700 border-orange-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <Badge variant="outline" className={map[status] ?? ""}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const EmailCampaignTable = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const url = useUrlState();
  const search = url.get("q") ?? "";
  const statusFilter =
    (url.get("status") as (typeof STATUS_OPTIONS)[number]) ?? "all";
  const [pendingDelete, setPendingDelete] = useState<EmailCampaign | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const paginated = usePaginatedQuery<EmailCampaign>({
    queryKey: queryKeys.emailCampaigns.list({
      page: 0,
      pageSize: 25,
      filters: { search, statusFilter },
    }),
    table: "email_campaigns",
    select:
      "id,name,status,frequency,scheduled_at,template_id,target_users,created_at,email_templates(name)",
    syncToUrl: true,
    orderBy: { column: "created_at", ascending: false },
    filters: (q) => {
      let next = q;
      if (statusFilter !== "all") next = next.eq("status", statusFilter);
      if (search) next = next.ilike("name", `%${search}%`);
      return next;
    },
  });

  const handleStatusChange = async (campaign: EmailCampaign, status: string) => {
    setUpdatingId(campaign.id);
    try {
      const { error } = await supabase
        .from("email_campaigns")
        .update({ status: status as CampaignStatus })
        .eq("id", campaign.id);
      if (error) throw error;
      toast({ title: "Status updated", description: `${campaign.name} → ${status}` });
      qc.invalidateQueries({ queryKey: ["emailCampaigns"] });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error",
        description: err?.message ?? "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("email_campaigns")
        .delete()
        .eq("id", pendingDelete.id);
      if (error) throw error;
      toast({ title: "Campaign deleted", description: pendingDelete.name });
      qc.invalidateQueries({ queryKey: ["emailCampaigns"] });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error",
        description: err?.message ?? "Failed to delete campaign",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const columns = useMemo<ColumnDef<EmailCampaign, unknown>[]>(
    () => [
      { accessorKey: "name", header: "Campaign" },
      {
        id: "template",
        header: "Template",
        cell: ({ row }) => row.original.email_templates?.name ?? "—",
      },
      {
        accessorKey: "frequency",
        header: "Frequency",
        cell: ({ row }) => (
          <span className="capitalize">{row.original.frequency}</span>
        ),
      },
      {
        id: "recipients",
        header: "Recipients",
        cell: ({ row }) => (row.original.target_users?.length ?? 0).toLocaleString(),
      },
      {
        accessorKey: "scheduled_at",
        header: "Scheduled",
        cell: ({ row }) =>
          row.original.scheduled_at
            ? new Date(row.original.scheduled_at).toLocaleString()
            : "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => statusBadge(row.original.status),
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const c = row.original;
          const busy = updatingId === c.id;
          return (
            <div className="flex justify-end items-center gap-2">
              <Select
                value={c.status}
                onValueChange={(v) => handleStatusChange(c, v)}
                disabled={busy}
              >
                <SelectTrigger className="h-8 w-[130px]">
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <SelectValue />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter((s) => s !== "all").map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-red-700 hover:bg-red-50"
                onClick={() => setPendingDelete(c)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatingId],
  );

  return (
    <div className="space-y-3">
      <ListToolbar
        search={search}
        onSearchChange={(v) => url.set({ q: v || null, page: 0 })}
        searchPlaceholder="Search campaigns by name…"
        filters={
          <Select
            value={statusFilter}
            onValueChange={(v) => url.set({ status: v === "all" ? null : v, page: 0 })}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        onExportCsv={() =>
          exportRowsAsCsv(
            "email-campaigns.csv",
            paginated.rows as unknown as Record<string, unknown>[],
          )
        }
      />

      <DataTable
        columns={columns}
        data={paginated.rows}
        isLoading={paginated.isLoading}
        isError={paginated.isError}
        onRetry={paginated.refetch}
        emptyTitle="No campaigns found"
        emptyDescription={
          statusFilter === "all"
            ? "Create your first email campaign using the form."
            : `No ${statusFilter} campaigns.`
        }
        getRowId={(row) => row.id}
      />

      <DataPagination
        page={paginated.page}
        pageSize={paginated.pageSize}
        total={paginated.total}
        pageCount={paginated.pageCount}
        onPageChange={paginated.setPage}
        onPageSizeChange={paginated.setPageSize}
        isFetching={paginated.isFetching}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete email campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>{pendingDelete?.name}</strong>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailCampaignTable;
